import {useModal} from 'UI/hooks/useModal';

import {getLinkData} from 'Application/utils/linkFieldUtils';

import type {TextImageButton} from 'types/payload-types';

/**
 * A custom hook that extracts and normalizes link properties for a text-image block's CTA button.
 * This hook processes the optional `cta` configuration and, if a button is defined, uses
 * `getLinkData` to resolve the correct link attributes (`href`, `label`, `rel`, and `target`).
 * These properties can then be passed directly into a button or link component for rendering.
 * If no CTA button is provided, the returned values will be `undefined`, ensuring safe usage
 * in conditional rendering scenarios.
 *
 * @param cta Optional CTA configuration object containing the button definition.
 * @returns An object containing the normalized CTA link properties: `href`, `label`, `rel`, and `target`.
 *
 * @example
 * ```tsx
 * const { href, label, target } = useTextImage(cta);
 *
 * return cta?.hasButton && (
 *   <Button as="link" href={href!} target={target}>
 *     {label}
 *   </Button>
 * );
 * ```
 */
export const useTextImage = (cta?: TextImageButton) => {
    const {href, label, rel, target} = getLinkData(cta?.button);
    const {closeModal, Modal, openModal} = useModal();

    return {
        closeModal,
        href,
        label,
        Modal,
        openModal,
        rel,
        target
    };
};